import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  CardMedia,
  Typography,
  Box,
  Rating,
  Chip,
  Container,
  CircularProgress,
  Alert,
  Button
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Styled component for the product card
const ProductCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
  cursor: 'pointer',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: theme.shadows[8],
  },
}));

const ProductImage = styled(CardMedia)({
  height: 200,
  objectFit: 'contain',
  padding: '16px',
  backgroundColor: '#f5f5f5',
});

const PriceText = styled(Typography)(({ theme }) => ({
  color: theme.palette.error.main,
  fontWeight: 'bold',
  fontSize: '1.25rem',
}));

const OriginalPriceText = styled(Typography)({
  textDecoration: 'line-through',
  color: '#666',
  marginLeft: '8px',
  fontSize: '0.9rem',
});

const RatingContainer = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  margin: '8px 0',
});

const CategoryChip = styled(Chip)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  left: 8,
  backgroundColor: theme.palette.primary.main,
  color: 'white',
  fontSize: '0.7rem',
  height: 24,
}));

// Comprehensive demo data for multiple categories
const demoProducts = {
  electronics: [
    {
      id: 1,
      name: 'Wireless Bluetooth Headphones',
      price: 79.99,
      originalPrice: 99.99,
      rating: 4.5,
      reviewCount: 128,
      image: 'https://via.placeholder.com/200x200/4A90E2/FFFFFF?text=Headphones',
      description: 'Noise-cancelling wireless headphones with 30hr battery',
      inStock: true,
      fastDelivery: true,
      discount: 20,
      category: 'electronics',
      brand: 'AudioTech'
    },
    {
      id: 2,
      name: 'Smartphone Pro Max',
      price: 899.99,
      originalPrice: 999.99,
      rating: 4.8,
      reviewCount: 456,
      image: 'https://via.placeholder.com/200x200/50E3C2/FFFFFF?text=Smartphone',
      description: 'Latest smartphone with triple camera and 5G',
      inStock: true,
      fastDelivery: true,
      discount: 10,
      category: 'electronics',
      brand: 'TechGadget'
    },
    {
      id: 3,
      name: 'Gaming Laptop',
      price: 1299.99,
      originalPrice: 1499.99,
      rating: 4.6,
      reviewCount: 289,
      image: 'https://via.placeholder.com/200x200/9013FE/FFFFFF?text=Gaming+Laptop',
      description: 'High-performance gaming laptop with RTX graphics',
      inStock: false,
      fastDelivery: false,
      discount: 13,
      category: 'electronics',
      brand: 'GameMaster'
    },
    {
      id: 4,
      name: 'Smart Watch Series 5',
      price: 249.99,
      originalPrice: 299.99,
      rating: 4.3,
      reviewCount: 167,
      image: 'https://via.placeholder.com/200x200/417505/FFFFFF?text=Smart+Watch',
      description: 'Fitness tracking smartwatch with heart rate monitor',
      inStock: true,
      fastDelivery: true,
      discount: 17,
      category: 'electronics',
      brand: 'FitTech'
    }
  ],
  bags: [
    {
      id: 5,
      name: 'Premium Leather Backpack',
      price: 79.99,
      originalPrice: 99.99,
      rating: 4.5,
      reviewCount: 128,
      image: 'https://via.placeholder.com/200x200/8B572A/FFFFFF?text=Leather+Bag',
      description: 'Water-resistant leather backpack with laptop compartment',
      inStock: true,
      fastDelivery: true,
      discount: 20,
      category: 'bags',
      brand: 'UrbanGear'
    },
    {
      id: 6,
      name: 'Travel Hiking Backpack 40L',
      price: 49.99,
      originalPrice: 69.99,
      rating: 4.2,
      reviewCount: 89,
      image: 'https://via.placeholder.com/200x200/7ED321/FFFFFF?text=Hiking+Bag',
      description: 'Lightweight hiking backpack with hydration system',
      inStock: true,
      fastDelivery: false,
      discount: 29,
      category: 'bags',
      brand: 'OutdoorPro'
    },
    {
      id: 7,
      name: 'Business Laptop Bag',
      price: 59.99,
      originalPrice: 79.99,
      rating: 4.7,
      reviewCount: 256,
      image: 'https://via.placeholder.com/200x200/4A4A4A/FFFFFF?text=Laptop+Bag',
      description: 'Professional laptop bag with multiple compartments',
      inStock: false,
      fastDelivery: true,
      discount: 25,
      category: 'bags',
      brand: 'ExecutiveStyle'
    }
  ],
  clothing: [
    {
      id: 8,
      name: 'Cotton T-Shirt Pack',
      price: 24.99,
      originalPrice: 34.99,
      rating: 4.1,
      reviewCount: 78,
      image: 'https://via.placeholder.com/200x200/BD10E0/FFFFFF?text=T-Shirt',
      description: 'Pack of 3 comfortable cotton t-shirts',
      inStock: true,
      fastDelivery: true,
      discount: 29,
      category: 'clothing',
      brand: 'ComfortWear'
    },
    {
      id: 9,
      name: 'Denim Jacket',
      price: 59.99,
      originalPrice: 79.99,
      rating: 4.4,
      reviewCount: 134,
      image: 'https://via.placeholder.com/200x200/4A90E2/FFFFFF?text=Denim+Jacket',
      description: 'Classic denim jacket for casual wear',
      inStock: true,
      fastDelivery: false,
      discount: 25,
      category: 'clothing',
      brand: 'DenimCo'
    }
  ],
  books: [
    {
      id: 10,
      name: 'The Complete Programming Guide',
      price: 29.99,
      originalPrice: 39.99,
      rating: 4.7,
      reviewCount: 89,
      image: 'https://via.placeholder.com/200x200/F5A623/FFFFFF?text=Programming+Book',
      description: 'Comprehensive guide to modern programming',
      inStock: true,
      fastDelivery: true,
      discount: 25,
      category: 'books',
      brand: 'TechPress'
    },
    {
      id: 11,
      name: 'Science Fiction Novel',
      price: 14.99,
      originalPrice: 19.99,
      rating: 4.5,
      reviewCount: 156,
      image: 'https://via.placeholder.com/200x200/9013FE/FFFFFF?text=Sci-Fi+Book',
      description: 'Bestselling science fiction adventure',
      inStock: true,
      fastDelivery: true,
      discount: 25,
      category: 'books',
      brand: 'FantasyPress'
    }
  ],
  home: [
    {
      id: 12,
      name: 'Smart Coffee Maker',
      price: 89.99,
      originalPrice: 119.99,
      rating: 4.6,
      reviewCount: 203,
      image: 'https://via.placeholder.com/200x200/8B572A/FFFFFF?text=Coffee+Maker',
      description: 'WiFi enabled smart coffee maker with app control',
      inStock: true,
      fastDelivery: true,
      discount: 25,
      category: 'home',
      brand: 'SmartHome'
    },
    {
      id: 13,
      name: 'Air Purifier',
      price: 149.99,
      originalPrice: 199.99,
      rating: 4.3,
      reviewCount: 98,
      image: 'https://via.placeholder.com/200x200/50E3C2/FFFFFF?text=Air+Purifier',
      description: 'HEPA air purifier for large rooms',
      inStock: true,
      fastDelivery: false,
      discount: 25,
      category: 'home',
      brand: 'CleanAir'
    }
  ]
};

// Search keyword to category mapping
const searchKeywordMap = {
  // Electronics keywords
  'headphone': 'electronics',
  'headphones': 'electronics',
  'phone': 'electronics',
  'smartphone': 'electronics',
  'laptop': 'electronics',
  'computer': 'electronics',
  'tablet': 'electronics',
  'camera': 'electronics',
  'watch': 'electronics',
  'smartwatch': 'electronics',
  'tv': 'electronics',
  'television': 'electronics',
  'gaming': 'electronics',
  'audio': 'electronics',
  'speaker': 'electronics',
  'electronic': 'electronics',
  
  // Bags keywords
  'bag': 'bags',
  'bags': 'bags',
  'backpack': 'bags',
  'backpacks': 'bags',
  'purse': 'bags',
  'handbag': 'bags',
  'luggage': 'bags',
  'suitcase': 'bags',
  'briefcase': 'bags',
  'tote': 'bags',
  
  // Clothing keywords
  'shirt': 'clothing',
  'tshirt': 'clothing',
  't-shirt': 'clothing',
  'pants': 'clothing',
  'jeans': 'clothing',
  'jacket': 'clothing',
  'hoodie': 'clothing',
  'sweater': 'clothing',
  'dress': 'clothing',
  'shoes': 'clothing',
  'sneakers': 'clothing',
  'clothes': 'clothing',
  'clothing': 'clothing',
  'fashion': 'clothing',
  
  // Books keywords
  'book': 'books',
  'books': 'books',
  'novel': 'books',
  'textbook': 'books',
  'guide': 'books',
  'magazine': 'books',
  'comic': 'books',
  
  // Home keywords
  'home': 'home',
  'kitchen': 'home',
  'furniture': 'home',
  'decor': 'home',
  'appliance': 'home',
  'coffee': 'home',
  'purifier': 'home',
  'vacuum': 'home',
  'lamp': 'home'
};

const MatchedItems = ({ searchQuery = '' }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [matchedCategory, setMatchedCategory] = useState('all');

  // Function to determine category based on search query
  const getMatchedCategory = (query) => {
    if (!query) return 'all';
    
    const queryLower = query.toLowerCase();
    
    // Check for exact matches first
    for (const [keyword, category] of Object.entries(searchKeywordMap)) {
      if (queryLower.includes(keyword)) {
        return category;
      }
    }
    
    return 'all'; // Return all products if no specific match
  };

  // Function to get category display name
  const getCategoryDisplayName = (category) => {
    const categoryNames = {
      electronics: 'Electronics',
      bags: 'Bags & Luggage',
      clothing: 'Clothing & Fashion',
      books: 'Books & Media',
      home: 'Home & Kitchen',
      all: 'All Products'
    };
    return categoryNames[category] || category;
  };

  // For demo purposes, we'll use the demo data
  useEffect(() => {
    const fetchProducts = async () => {
      if (!searchQuery.trim()) {
        // If no search query, show all products
        const allProducts = Object.values(demoProducts).flat();
        setProducts(allProducts);
        setMatchedCategory('all');
        return;
      }

      setLoading(true);
      setError(null);
      
      try {
        // Simulate API call delay
        await new Promise(resolve => setTimeout(resolve, 800));
        
        const category = getMatchedCategory(searchQuery);
        setMatchedCategory(category);
        
        // DEMO: Using demo data based on matched category
        if (category === 'all') {
          // Search across all categories
          const allProducts = Object.values(demoProducts).flat();
          const filteredProducts = allProducts.filter(product =>
            product.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            product.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
            product.brand.toLowerCase().includes(searchQuery.toLowerCase())
          );
          setProducts(filteredProducts);
        } else {
          // Show products from matched category
          setProducts(demoProducts[category] || []);
        }
        
        /*
        // BACKEND API CALL - UNCOMMENT AND IMPLEMENT FOR REAL USE
        
        const response = await fetch(`/api/products/search?q=${encodeURIComponent(searchQuery)}`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });

        if (!response.ok) {
          throw new Error('Failed to fetch products');
        }

        const data = await response.json();
        setProducts(data.products);
        
        // Extract category from backend response if available
        if (data.category) {
          setMatchedCategory(data.category);
        }
        */
        
      } catch (err) {
        setError(err.message);
        // Fallback to showing all products in case of error
        const allProducts = Object.values(demoProducts).flat();
        setProducts(allProducts);
        setMatchedCategory('all');
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, [searchQuery]);

  const handleProductClick = (productId) => {
    // Handle product click - navigate to product detail page
    // console.log('Product clicked:', productId);
    // navigate(`/product/${productId}`);
  };

  const formatPrice = (price) => {
    return `$${price.toFixed(2)}`;
  };

  const handleCategoryFilter = (category) => {
    if (category === 'all') {
      const allProducts = Object.values(demoProducts).flat();
      setProducts(allProducts);
    } else {
      setProducts(demoProducts[category] || []);
    }
    setMatchedCategory(category);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Searching for "{searchQuery}"...
        </Typography>
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      {/* Search Results Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          {searchQuery ? `Search Results for "${searchQuery}"` : 'All Products'}
        </Typography>
        
        {searchQuery && (
          <Typography variant="subtitle1" color="text.secondary" sx={{ mb: 2 }}>
            Showing {products.length} products in {getCategoryDisplayName(matchedCategory)}
          </Typography>
        )}

        {/* Category Filter Buttons */}
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 3 }}>
          <Button
            variant={matchedCategory === 'all' ? 'contained' : 'outlined'}
            size="small"
            onClick={() => handleCategoryFilter('all')}
          >
            All Products
          </Button>
          {Object.keys(demoProducts).map(category => (
            <Button
              key={category}
              variant={matchedCategory === category ? 'contained' : 'outlined'}
              size="small"
              onClick={() => handleCategoryFilter(category)}
            >
              {getCategoryDisplayName(category)}
            </Button>
          ))}
        </Box>
      </Box>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      <Grid container spacing={3}>
        {products.map((product) => (
          <Grid item xs={12} sm={6} md={4} lg={3} key={product.id}>
            <ProductCard onClick={() => handleProductClick(product.id)}>
              {/* Product Image with Category Chip */}
              <Box sx={{ position: 'relative' }}>
                <ProductImage
                  component="img"
                  image={product.image}
                  alt={product.name}
                  onError={(e) => {
                    e.target.src = 'https://via.placeholder.com/200x200/CCCCCC/666666?text=No+Image';
                  }}
                />
                <CategoryChip 
                  label={getCategoryDisplayName(product.category)} 
                  size="small"
                />
              </Box>
              
              <CardContent sx={{ flexGrow: 1, p: 2 }}>
                {/* Brand */}
                <Typography variant="caption" color="primary" fontWeight="bold">
                  {product.brand}
                </Typography>
                
                {/* Product Name */}
                <Typography variant="h6" component="h2" gutterBottom sx={{ 
                  fontSize: '1rem',
                  height: '48px',
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical'
                }}>
                  {product.name}
                </Typography>
                
                {/* Rating */}
                <RatingContainer>
                  <Rating 
                    value={product.rating} 
                    precision={0.1} 
                    size="small" 
                    readOnly 
                  />
                  <Typography variant="body2" color="text.secondary">
                    ({product.reviewCount})
                  </Typography>
                </RatingContainer>
                
                {/* Price */}
                <Box display="flex" alignItems="center" mb={1}>
                  <PriceText>
                    {formatPrice(product.price)}
                  </PriceText>
                  {product.originalPrice > product.price && (
                    <OriginalPriceText>
                      {formatPrice(product.originalPrice)}
                    </OriginalPriceText>
                  )}
                  {product.discount && (
                    <Chip 
                      label={`${product.discount}% off`} 
                      size="small" 
                      color="error" 
                      sx={{ ml: 1, height: 20, fontSize: '0.7rem' }}
                    />
                  )}
                </Box>
                
                {/* Description */}
                <Typography 
                  variant="body2" 
                  color="text.secondary" 
                  sx={{ 
                    mb: 2,
                    height: '40px',
                    overflow: 'hidden',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical'
                  }}
                >
                  {product.description}
                </Typography>
                
                {/* Badges */}
                <Box display="flex" gap={1} flexWrap="wrap">
                  {!product.inStock && (
                    <Chip 
                      label="Out of Stock" 
                      size="small" 
                      variant="outlined" 
                      color="error"
                    />
                  )}
                  {product.fastDelivery && (
                    <Chip 
                      label="Fast Delivery" 
                      size="small" 
                      variant="outlined" 
                      color="success"
                    />
                  )}
                </Box>
              </CardContent>
            </ProductCard>
          </Grid>
        ))}
      </Grid>
      
      {products.length === 0 && !loading && (
        <Box textAlign="center" py={8}>
          <Typography variant="h6" color="text.secondary">
            No products found for "{searchQuery}"
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Try different keywords or browse all categories
          </Typography>
          <Button 
            variant="contained" 
            sx={{ mt: 2 }}
            onClick={() => handleCategoryFilter('all')}
          >
            Browse All Products
          </Button>
        </Box>
      )}
    </Container>
  );
};

export default MatchedItems;